import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { authAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { countries, Country, getCountryByCode } from '../../data/countries';
import useDocumentTitle from '../../hooks/useDocumentTitle';

const RegisterPage: React.FC = () => {
  useDocumentTitle('Register');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    country: '',
    country_code: '',
    phone: '',
  });
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const state = location.state as { email?: string; verified?: boolean };

  useEffect(() => {
    if (!state?.email || !state?.verified) {
      toast.error('Please verify your email first');
      navigate('/signup');
      return;
    }
    setFormData(prev => ({ ...prev, email: state.email || '' }));
  }, [location, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const countryCode = e.target.value;
    const country = getCountryByCode(countryCode);

    if (country) {
      setSelectedCountry(country);
      setFormData(prev => ({
        ...prev,
        country: countryCode,
        country_code: country.phoneCode,
      }));
    } else {
      setSelectedCountry(null);
      setFormData(prev => ({
        ...prev,
        country: '',
        country_code: '',
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Please enter your full name');
      return;
    }

    if (!formData.country) {
      toast.error('Please select your country');
      return;
    }

    if (!formData.phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }

    setIsLoading(true);
    try {
      const submitData = {
        name: formData.name,
        email: formData.email,
        country: formData.country,
        phone: `${formData.country_code}${formData.phone}`,
      };

      const response = await authAPI.register(submitData);
      if (response.data.status === 'success') {
        const { user, token } = response.data;
        login(user, token);
        toast.success('Registration successful! Welcome to CloudAyn');

        // Redirect to organization setup with country info for auto currency
        navigate('/setup/organization', {
          state: {
            userCountry: formData.country,
            suggestedCurrency: selectedCountry?.currency
          }
        });
      } else {
        toast.error(response.data.message || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  if (!state?.email || !state?.verified) {
    return null;
  }

  return (
    <div className="min-vh-100 d-flex align-items-center bg-light">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-8 col-lg-6">
            <div className="card shadow">
              <div className="card-body p-4">
                {/* Header */}
                <div className="text-center mb-4">
                  <h1 className="h3 text-primary fw-bold">CloudAyn</h1>
                  <p className="text-muted">Complete your registration</p>
                  <small className="text-success">Email verified</small>
                </div>

                {/* Registration Form */}
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="name" className="form-label">
                          Full Name <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Enter your full name"
                          required
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label">
                          Email Address
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          id="email"
                          name="email"
                          value={formData.email}
                          disabled
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="country" className="form-label">
                      Country <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-select"
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleCountryChange}
                      required
                      disabled={isLoading}
                    >
                      <option value="">Select your country</option>
                      {countries.map(country => (
                        <option key={country.code} value={country.code}>
                          {country.flag} {country.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="phone" className="form-label">
                      Phone Number <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text" style={{ minWidth: '90px' }}>
                        {selectedCountry ? (
                          <>
                            {selectedCountry.flag} {selectedCountry.phoneCode}
                          </>
                        ) : (
                          <span className="text-muted">+--</span>
                        )}
                      </span>
                      <input
                        type="tel"
                        className="form-control"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          setFormData(prev => ({ ...prev, phone: value }));
                        }}
                        placeholder="Enter phone number"
                        required
                        disabled={isLoading || !formData.country}
                      />
                    </div>
                    {!formData.country && (
                      <div className="form-text text-muted">
                        Select a country first to enter phone number
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Creating Account...
                      </>
                    ) : (
                      'Complete Registration'
                    )}
                  </button>
                </form>

                {/* Terms */}
                <div className="text-center mt-3">
                  <small className="text-muted">
                    By registering, you agree to our{' '}
                    <a href="#" className="text-primary text-decoration-none">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="#" className="text-primary text-decoration-none">
                      Privacy Policy
                    </a>
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
